import { useOktaAuth } from '@okta/okta-react';
import { Divider } from 'antd';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { IdpType } from 'api/account';
import { Button } from 'components/atoms/button';
import { LinkText, UserInfo } from 'components/atoms/form-elements';
import {
  BackgroundColor,
  InputBorder,
  InputFocus,
} from 'components/atoms/input/input';
import { Layout } from 'components/atoms/layout';
import { PageLoading } from 'components/atoms/loading';
import { ReactComponent as Google } from 'components/atoms/vendor-loign/google.svg';
import { CustomerLogin } from 'components/molecules/session-setup/customer-login-link-generate';
import {
  BodyShort01,
  Label01,
  ProductiveHeading04,
  Text01,
} from 'components/styles';
import { RoutePaths } from 'constant';
import { useTranslate } from 'hooks/use-translation';
import { useAccountMetaQuery } from 'queries';
import { CompanyLogo } from 'routes/auth/common/company-logo';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import { useIsSamlMandatory } from '../saml';
import './login.css';

const OktaSignInWidget = lazyWithRetry(() =>
  import('./okta-signin-widget').then(({ SignInWidget }) => ({
    default: SignInWidget,
  })),
);

const { Content } = Layout;

export const Container = styled(Content)`
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 88px 120px;
  align-items: flex-start;
`;

export const WelcomeHeading = styled.strong`
  ${ProductiveHeading04};
  font-weight: var(--heading-8-weight);
`;

export const Heading = styled.div`
  ${ProductiveHeading04};
  font-size: var(--heading-5-size);
`;

const HelperLink = styled(Link)`
  ${BodyShort01};
`;

const ForgotPasswordLink = styled(Link)`
  ${BodyShort01};
  position: absolute;
  right: 0;
  bottom: calc(var(--spacing-10) + var(--spacing-05) + 2.5 * var(--spacing-01));
  z-index: var(--zindex-elevated);
`;

export const HeadingContainer = styled.div`
  display: flex;
  gap: var(--spacing-05);
  align-items: center;
  margin-bottom: var(--layout-05);
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  & button {
    height: 50px;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
`;

const StyledDivider = styled(Divider)`
  span {
    transform: translateY(50%);
    font-size: var(--font-size-sm);
    color: var(--gray-50);
  }
`;

const WidgetContainer = styled.div`
  width: fit-content;
  position: relative;
`;

export const HelperTitle = styled.div`
  margin-bottom: var(--spacing-04);
  font-size: var(--font-size-lg);
  font-weight: var(--heading-6-weight);
`;

const StyledSignInWidget = styled(OktaSignInWidget)`
  #okta-sign-in {
    margin-bottom: 0 !important;
    &.auth-container {
      &.main-container {
        box-shadow: none;
        border: none;
        margin-top: 0;
      }
      * {
        font-family: var(--font-family) !important;
      }
    }
    .auth-content {
      padding: 0;
    }
    .auth-footer {
      display: none;
    }
    label {
      ${Label01};
    }

    #okta-sign-in .o-form .input-fix {
      background: var(--gray-10) !important;
    }
    .o-form-fieldset {
      margin-bottom: var(--spacing-06) !important;

      :nth-child(1) {
        margin-bottom: var(--spacing-05) !important;
      }
      :nth-child(2) {
        margin-bottom: var(--spacing-03) !important;
      }
    }

    .o-form-content {
      padding-bottom: 0 !important;
    }

    .o-form-button-bar {
      padding-bottom: var(--spacing-03);
    }

    .o-form .okta-form-input-field {
      width: 100%;
      padding: 0 var(--spacing-05);
      ${InputBorder};
      ${BodyShort01};
      ${Text01};
      ${BackgroundColor};
      height: var(--input-height-lg);

      &.focused-input {
        ${InputFocus};
      }
    }
    /* For signin button*/
    #okta-signin-submit.button.button-primary {
      text-align: left;
      border-radius: 0;
      border-color: var(--blue-60);
      background: var(--blue-60);
      color: var(--white-100);
    }
  }

  .custom-checkbox {
    color: var(--gray-80) !important;
  }

  #okta-sign-in .focused-input {
    box-shadow: none;
  }
  #okta-sign-in .social-auth-button {
    padding: 0 0 0 var(--spacing-05);
    background: var(--blue-60);
    color: var(--white-100);
  }
`;

export const LoginPage: FC = () => {
  const { data, isLoading, isError } = useAccountMetaQuery();
  const isSamlMandate = useIsSamlMandatory();
  const translate = useTranslate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const customerParam = params.get('customer');
  const partnerParam = params.get('partner');

  const isSamlEnabled = useMemo(
    () => !!(data?.okta.idps ?? []).filter((idp) => idp.enabled).length,
    [data?.okta.idps],
  );

  const { oktaAuth } = useOktaAuth();
  const idpId = data?.okta.idps?.find((idp) => idp.enabled)?.idpId;

  return isLoading ? (
    <PageLoading />
  ) : data && !isError ? (
    <Layout>
      {customerParam ? (
        <CustomerLogin />
      ) : (
        <Container>
          <HeadingContainer>
            {data && <CompanyLogo data={data.company} />}
            <TextContainer>
              <WelcomeHeading>
                <FormattedMessage id="welcome" />
              </WelcomeHeading>
              <Heading>
                <FormattedMessage
                  id="login.title"
                  values={{
                    companyName: data.company.companyName,
                  }}
                />
              </Heading>
            </TextContainer>
          </HeadingContainer>

          {!customerParam && !partnerParam && isSamlEnabled && !isSamlMandate && (
            <ButtonContainer>
              {(data.okta.idps ?? [])
                .filter((idp) => idp.enabled)
                .map((idp) => {
                  return (
                    <Button
                      key={idp.idpId}
                      icon={idp.idpType === IdpType.Google && <Google />}
                      onClick={() => {
                        oktaAuth.signInWithRedirect({
                          idp: idpId,
                        });
                      }}
                    >
                      {idp.idpType === IdpType.Google
                        ? translate('google.sign-in')
                        : translate('sign_in.sso')}
                    </Button>
                  );
                })}

              <StyledDivider orientation="center">
                <FormattedMessage id="sign_in_with_your_email" />
              </StyledDivider>
            </ButtonContainer>
          )}

          {partnerParam && (
            <HelperTitle>
              <FormattedMessage id="signing_in_as_partner" />
            </HelperTitle>
          )}

          {!customerParam && (
            <WidgetContainer>
              <StyledSignInWidget
                oktaMetaData={data.okta}
                isSamlMandatory={isSamlMandate}
                isSamlEnabled={isSamlEnabled}
                isPartner={partnerParam}
              />

              {!isSamlMandate && (
                <ForgotPasswordLink to={RoutePaths.forgotPassword}>
                  <FormattedMessage id="forgot_password" />
                </ForgotPasswordLink>
              )}
            </WidgetContainer>
          )}

          {!customerParam && !partnerParam && (
            <LinksContainer>
              <HelperLink to={`${RoutePaths.login}?customer=true`}>
                <FormattedMessage
                  id="are_you_a_customer"
                  values={{
                    companyName: data.company.companyName,
                  }}
                />
              </HelperLink>{' '}
              {isSamlMandate && (
                <>
                  /{' '}
                  <HelperLink to={`${RoutePaths.login}?partner=true`}>
                    <FormattedMessage
                      id="are_you_a_partner"
                      values={{
                        companyName: data.company.companyName,
                      }}
                    />
                  </HelperLink>
                </>
              )}
            </LinksContainer>
          )}

          {customerParam || partnerParam ? (
            <UserInfo>
              <FormattedMessage
                id="forgot_password.cta_back"
                values={{
                  a: (chunks) => (
                    <Link to={RoutePaths.login}>
                      <LinkText>{chunks}</LinkText>
                    </Link>
                  ),
                }}
              />
            </UserInfo>
          ) : (
            <></>
          )}
        </Container>
      )}
    </Layout>
  ) : (
    <></>
  );
};
